import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Kbd, Loader, Modal, Text, TextInput, UnstyledButton } from '@mantine/core';
import { useDebouncedValue, useHotkeys } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import {
  IconBox,
  IconBuildingStore,
  IconCoin,
  IconSearch,
  IconUser,
  IconUsers,
  type Icon,
} from '@tabler/icons-react';
import { apiClient } from '../../api/client';
import { navSections, settingsItem } from '../AppShell/navItems';

// Ctrl/⌘+K command palette: nav items (instant, client-side) + live
// tenant-scoped entity search (debounced, one aggregated endpoint).
// Selecting navigates to the target page.

interface SearchHit {
  type: string;
  id: string;
  title: string;
  subtitle: string | null;
  route: string;
}

const TYPE_ICON: Record<string, Icon> = {
  item: IconBox,
  customer: IconUser,
  supplier: IconBuildingStore,
  employee: IconUsers,
  account: IconCoin,
};

interface Row {
  key: string;
  label: string;
  hint?: string;
  route: string;
  icon: Icon;
  group: string;
}

export function GlobalSearch({ variant = 'compact' }: { variant?: 'compact' | 'hero' } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced] = useDebouncedValue(query, 200);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useHotkeys([['mod+K', () => setOpen(true)]]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Nav items matched client-side.
  const navRows = useMemo<Row[]>(() => {
    const q = query.trim().toLowerCase();
    const all = [...navSections.flatMap((s) => s.items), settingsItem];
    return all
      .map((item) => ({
        key: `nav:${item.to}`,
        label: t(item.labelKey),
        route: item.to,
        icon: item.icon,
        group: t('search.pages'),
      }))
      .filter((r) => q === '' || r.label.toLowerCase().includes(q))
      .slice(0, 6);
  }, [query, t]);

  const { data, isFetching } = useQuery({
    queryKey: ['global-search', debounced] as const,
    queryFn: async () =>
      (await apiClient.get<{ hits: SearchHit[] }>('/api/search', { params: { q: debounced } })).data,
    enabled: debounced.trim().length >= 2,
  });

  const entityRows = useMemo<Row[]>(
    () =>
      (data?.hits ?? []).map((h) => ({
        key: `${h.type}:${h.id}`,
        label: h.title,
        hint: h.subtitle ?? undefined,
        route: h.route,
        icon: TYPE_ICON[h.type] ?? IconSearch,
        group: t(`search.groups.${h.type}`),
      })),
    [data, t],
  );

  const rows = useMemo(() => [...navRows, ...entityRows], [navRows, entityRows]);

  useEffect(() => {
    setActive(0);
  }, [rows.length]);

  const choose = (row: Row) => {
    setOpen(false);
    navigate(row.route);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && rows[active]) {
      e.preventDefault();
      choose(rows[active]);
    }
  };

  // Group rows for rendering while keeping a flat index for keyboard nav.
  let flatIndex = -1;
  const groups = Array.from(new Set(rows.map((r) => r.group)));

  return (
    <>
      {variant === 'hero' ? (
        <UnstyledButton
          onClick={() => setOpen(true)}
          className="flex w-full items-center gap-3 rounded-xl border border-[var(--wheels-border)] bg-white px-5 h-14 text-[var(--wheels-ink-muted)] shadow-sm hover:border-[var(--wheels-accent)] transition-colors"
          aria-label={t('search.open')}
        >
          <IconSearch size={22} stroke={1.6} />
          <Text size="md">{t('search.placeholder')}</Text>
          <Kbd size="sm" className="ms-auto">⌘K</Kbd>
        </UnstyledButton>
      ) : (
        <UnstyledButton
          onClick={() => setOpen(true)}
          className="hidden sm:flex items-center gap-2 rounded-md border border-[var(--wheels-border)] px-3 h-9 text-[var(--wheels-ink-muted)] hover:border-[var(--wheels-accent)] transition-colors"
          aria-label={t('search.open')}
        >
          <IconSearch size={16} stroke={1.6} />
          <Text size="sm">{t('search.placeholder')}</Text>
          <Kbd size="xs">⌘K</Kbd>
        </UnstyledButton>
      )}

      <Modal
        opened={open}
        onClose={() => setOpen(false)}
        withCloseButton={false}
        size="lg"
        padding={0}
        yOffset="12vh"
      >
        <TextInput
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          onKeyDown={onKeyDown}
          placeholder={t('search.placeholder')}
          size="md"
          variant="unstyled"
          leftSection={<IconSearch size={18} stroke={1.6} />}
          rightSection={isFetching ? <Loader size="xs" color="wheelsRed" /> : null}
          className="px-4 border-b border-[var(--wheels-border)]"
        />
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {rows.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">
              {query.trim().length >= 2 ? t('search.noResults') : t('search.hint')}
            </Text>
          ) : (
            groups.map((group) => (
              <div key={group}>
                <Text size="xs" c="dimmed" fw={600} px="sm" pt="sm" pb={4}>
                  {group}
                </Text>
                {rows
                  .filter((r) => r.group === group)
                  .map((row) => {
                    flatIndex++;
                    const idx = flatIndex;
                    const RowIcon = row.icon;
                    return (
                      <UnstyledButton
                        key={row.key}
                        onClick={() => choose(row)}
                        onMouseEnter={() => setActive(idx)}
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-start"
                        style={{
                          backgroundColor: idx === active ? 'var(--wheels-bg)' : undefined,
                        }}
                      >
                        <RowIcon size={18} stroke={1.6} color="var(--wheels-ink-muted)" />
                        <div className="flex-1 min-w-0">
                          <Text size="sm" c="var(--wheels-ink-strong)" truncate>
                            {row.label}
                          </Text>
                          {row.hint && (
                            <Text size="xs" c="dimmed" truncate>
                              {row.hint}
                            </Text>
                          )}
                        </div>
                      </UnstyledButton>
                    );
                  })}
              </div>
            ))
          )}
        </div>
      </Modal>
    </>
  );
}
